import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, TableCellsMerge, Sheet, FileSliders } from "lucide-react";
import axiosInstance from "../../../../../../src/axiosInstanceSetup";
import ReportLineage from "../../../Domain/ReportLIneage";
import { ReportUsagetrend } from "../../../Domain/ReportUsagetrend";
import { ReportKPITab } from "../../../Domain/AssetsDetailsUI";
import heartLoading from "../../../../../assests/heartLoading.gif";
import { Typography, Box } from '@mui/material';


const ViewKpiModal = ({ showKpiModal, setShowKpiModal, contract }) => {
  const [activeTab, setActiveTab] = useState("Report KPI");

  const [kpiData, setKpiData] = useState([]);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [accumulatedUsers, setAccumulatedUsers] = useState([]);
  const [usagePage, setUsagePage] = useState(1);
  const [businessHasMore, setBusinessHasMore] = useState(false);
  const [isFetchingMoreBusiness, setIsFetchingMoreBusiness] = useState(false);
  const [reportSortBy, setReportSortBy] = useState("");
  const [reportSortOrder, setReportSortOrder] = useState("desc");
  const [reportColumnSearch, setReportColumnSearch] = useState({});

  const reportName = contract?.reportName || contract?.report_name || "";
  const l5 = reportName;
  const reportSourceType = contract?.source_type || "uc";

  const fetchKpiData = useCallback(async () => {
    if (!reportName) return;
    setKpiLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/reportkpi`,
        { params: { report_name: reportName } }
      );
      if (response.status === 200) {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data?.data || response.data?.results || [];
        setKpiData(data);
      }
    } catch (e) {
      console.error("Error fetching KPI data:", e);
      setKpiData([]);
    } finally {
      setKpiLoading(false);
    }
  }, [reportName]);

  const fetchUsageWithParams = useCallback(
    async (page, sortBy, sortOrder, columnSearch, append = false) => {
      if (!l5) return;
      if (append) {
        setIsFetchingMoreBusiness(true);
      } else {
        setUsageLoading(true);
      }
      try {
        const params = new URLSearchParams();
        params.append("page", String(page));
        params.append("page_size", "10");
        params.append("sort_order", sortOrder || "desc");
        if (sortBy) params.append("sort_by", sortBy);
        const activeSearchKey = Object.keys(columnSearch || {})[0];
        if (activeSearchKey && columnSearch[activeSearchKey]) {
          params.append("search_by", activeSearchKey);
          params.append("search_value", columnSearch[activeSearchKey]);
        }
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/report_data_insights/${encodeURIComponent(l5)}?${params.toString()}`
        );
        if (response.status === 200) {
          if (append) {
            setAccumulatedUsers((prev) => [
              ...prev,
              ...(response.data.business_users || []),
            ]);
          } else {
            setUsageData(response.data);
            setAccumulatedUsers(response.data.business_users || []);
            setUsagePage(page);
          }
          const pagination = response.data.pagination;
          setBusinessHasMore(page < (pagination?.total_pages ?? 1));
          // sync page for append path too (fixes desync bug)
          if (append) setUsagePage(page);
        }
      } catch (e) {
        console.error("Error fetching usage data:", e);
      } finally {
        setIsFetchingMoreBusiness(false);
        setUsageLoading(false);
      }
    },
    [l5]
  );

  useEffect(() => {
    if (!showKpiModal) return;
    if (activeTab === "Report KPI") {
      fetchKpiData();
    }
  }, [activeTab, showKpiModal, fetchKpiData]);

  useEffect(() => {
    if (!showKpiModal || activeTab !== "Report Usage") return;
    setAccumulatedUsers([]);
    setUsagePage(1);
    fetchUsageWithParams(1, reportSortBy, reportSortOrder, reportColumnSearch);
  }, [activeTab, showKpiModal]);

  const handleReportSortChange = (key, order) => {
    setReportSortBy(key);
    setReportSortOrder(order);
    setAccumulatedUsers([]);
    setUsagePage(1);
    fetchUsageWithParams(1, key, order, reportColumnSearch);
  };

  const handleReportSearchChange = (key, value) => {
    const newSearch = value ? { [key]: value } : {};
    setReportColumnSearch(newSearch);
    setAccumulatedUsers([]);
    setUsagePage(1);
    fetchUsageWithParams(1, reportSortBy, reportSortOrder, newSearch);
  };

  const loadMoreBusiness = async () => {
    if (!l5 || isFetchingMoreBusiness) return;
    const nextPage = usagePage + 1;
    await fetchUsageWithParams(
      nextPage,
      reportSortBy,
      reportSortOrder,
      reportColumnSearch,
      true
    );
  };

  const handleClose = () => {
    setShowKpiModal(false);
    setActiveTab("Report KPI");
    setKpiData([]);
    setUsageData(null);
    setAccumulatedUsers([]);
    setUsagePage(1);
    setReportSortBy("");
    setReportSortOrder("desc");
    setReportColumnSearch({});
  };

  const tabs = [
    { id: "Report KPI", label: "Report KPI", icon: TableCellsMerge },
    { id: "Report Usage", label: "Report Usage", icon: Sheet },
    { id: "Report Lineage", label: "Report Lineage", icon: FileSliders },
  ];

  if (!showKpiModal) return null;

  return (
    <div
      className="fixed inset-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg w-[1230px] h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Report Details
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6 flex-shrink-0">
          <nav className="flex space-x-8 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap flex-shrink-0 flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                  ? "border-[#FF03F3] text-[#FF03F3]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {activeTab === "Report KPI" && (
            <>
              {kpiLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="flex flex-col items-center space-y-3">
                    <img src={heartLoading} alt="Loading..." className="h-32 w-32" />
                    <span className="text-gray-700 text-2xl text-center">
                      Loading Report data...
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  {(contract?.reportName || contract?.report_name) && (
                    <Typography
                      variant="h5"
                      sx={{
                        color: "#FF03F3",
                        fontWeight: 700,
                        fontSize: "1.5rem",
                        marginBottom: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: "4px",
                          height: "24px",
                          backgroundColor: "#FF03F3",
                          borderRadius: "2px",
                        }}
                      />
                      {contract?.reportName || contract?.report_name}
                    </Typography>
                  )}
                  <ReportKPITab data={kpiData} loading={false} />
                </>
              )}
            </>
          )}

          {activeTab === "Report Usage" && (
            <ReportUsagetrend
              usageData={usageData}
              loading={usageLoading}
              accumulatedUsers={accumulatedUsers}
              loadMoreBusiness={loadMoreBusiness}
              businessHasMore={businessHasMore}
              isFetchingMoreBusiness={isFetchingMoreBusiness}
              sortBy={reportSortBy}
              sortOrder={reportSortOrder}
              columnSearch={reportColumnSearch}
              onSortChange={handleReportSortChange}
              onSearchChange={handleReportSearchChange}
              hasInteracted={false}
            />
          )}

          {activeTab === "Report Lineage" && (
            <ReportLineage
              reportName={l5}
              sourceType={reportSourceType}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewKpiModal;